import { createSelector } from 'reselect'
import { getPageData, getPageParams } from 'selectors/page'
import { CURATOR } from 'lib/user/constants'
import { capitalizeString } from 'lib/helpers'
import { getUserID, isAuthenticated as getIsAuthenticated } from 'selectors/user/session'
export { getIsArtist, isSessionSet } from 'selectors/user/session'
export { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import {
  getAlgoliaDataPaginationCurrent,
  getAlgoliaDataPaginationTotal,
  getAlgoliaDataHitsResults,
} from 'selectors/algolia2/data'
import { StudioImagesType, CollectionType } from '../types'
export const getPageParamUserId = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.userId || ''
)
export const getPageParamSection = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.section || 'profile'
)
export const getIsUsersArtworksAccount = createSelector(
  [getPageParams, getUserID, getIsAuthenticated],
  (params, userId, isAuthenticated): boolean =>
    (isAuthenticated && Number(userId) === Number(params.userId || 0)) || false
)
export const getAccountData = createSelector(
  [getPageData],
  (pageData): Record<string, any> => pageData.accountData || {}
)
export const getAboutArtist = createSelector(
  [getAccountData],
  (accountData): Record<string, any> => accountData.aboutArtist || {}
)
export const getAccountType = createSelector(
  [getPageData],
  (pageData): Record<string, any> => pageData.accountType || 'Artist'
)
export const getFullName = createSelector(
  [getAccountData],
  ({ firstName, lastName }): string => `${firstName} ${lastName}`
)
export const getCountry = createSelector(
  [getAccountData],
  (accountData): string => accountData.country || ''
)
export const getUserName = createSelector(
  [getAccountData],
  (accountData): string => accountData.userName || ''
)
export const getAvatar = createSelector(
  [getAccountData],
  (accountData): string => accountData.avatar || ''
)
export const getAvatarLarge = createSelector(
  [getAccountData],
  (accountData): string => accountData.avatarLarge || ''
)
export const getCanIndex = createSelector(
  [getAccountData],
  (accountData): boolean => accountData.canIndex || false
)
export const getAccountUserID = createSelector(
  [getAccountData],
  (accountData): number => accountData.userId || 0
)
export const getEmail = createSelector(
  [getAccountData],
  (accountData): string => accountData.email || ''
)
export const getSocialLinks = createSelector(
  [getAccountData],
  (accountData): Record<string, any> => accountData.socialLinks || {}
)
export const getHeroImages = createSelector(
  [getAccountData],
  (accountData): Record<string, any> => accountData.hero || {}
)
export const getArtworks = createSelector(
  [getAccountData],
  (accountData): Array<Record<string, any>> => accountData.artworks || []
)
export const getArtworksLength = createSelector(
  [getAccountData],
  (accountData): number => accountData.artworks?.length || 0
)
export const getCollections = createSelector(
  [getAccountData],
  (accountData): Array<Record<string, any>> => accountData.collections || []
)
export const getFavorites = createSelector(
  [getAccountData],
  (accountData): Array<Record<string, any>> => accountData.favorites || []
)
export const getInsideTheStudioImages = createSelector(
  [getAccountData],
  (accountData): StudioImagesType => accountData.insideTheStudioImages || []
)
export const getArtworksTotal = createSelector(
  [getAccountData],
  (accountData): number => accountData.artworksTotal || 0
)
export const getBadgeTitles = createSelector(
  [getAccountData],
  (accountData): Array<string> =>
    accountData.badges ? accountData.badges.map((item) => item.title) : '' || []
)
export const getBadges = createSelector(
  [getAccountData],
  (accountData): Array<Record<string, any>> => accountData.badges || []
)
export const getArtistDataLayer = createSelector(
  [getAccountUserID, getFullName, getCountry, getBadgeTitles],
  (userId, fullName, country, badgeTitles): Record<string, any> => ({
    environment: 'production',
    event: 'pagetype',
    pagetype: 'userProfile',
    subpagetype: 'profile',
    'artist country': country,
    'artist id': userId,
    'artist name': fullName,
    badges: badgeTitles,
  })
)
export const getCuratorDataLayer = createSelector(
  [getAccountUserID, getFullName, getCountry],
  (userId, fullName, country): Record<string, any> => ({
    environment: 'production',
    event: 'pagetype',
    pagetype: 'curatorProfile',
    subpagetype: 'profile',
    'curator country': country,
    'user id': userId,
    'curator name': fullName,
  })
)
export const getAllArtworksDataLayer = createSelector(getPageData, (data) => {
  const layerData = {
    pagetype: 'artistProfile',
    subpagetype: 'artworks',
    subsubpagetype: '',
  }
  layerData.subsubpagetype = `${data.firstName} ${data.lastName}` || ''
  return layerData
})
export const getCanRequestCommission = createSelector(
  [getCanIndex, getUserID, getAccountUserID],
  (canIndex, sessionUserId, accountUserId): boolean => {
    const myOwnAccount = sessionUserId === accountUserId
    return !myOwnAccount && canIndex
  }
)
export const getProfileMetaData = createSelector(
  [getAccountType, getAvatar, getFullName, getArtworksTotal],
  (accountType, avatar, fullName, artworksTotal): Record<string, any> => {
    const isAccountTypeCurator = accountType === CURATOR
    const descriptionText = isAccountTypeCurator ? 'curated by' : 'like'
    const title = `${capitalizeString(fullName)} | Saatchi Art`
    const description = `View ${fullName}’s Profile on Saatchi Art. Find art for sale at great prices from artists including Paintings, Photography, Sculpture, and Prints by Top Emerging Artists ${descriptionText} ${fullName}.`
    return {
      description,
      title,
      robots: Boolean(artworksTotal) || isAccountTypeCurator,
      og: {
        description,
        image: avatar,
        title,
        type: 'article',
      },
      preloadImages: [],
      schema: {
        webpage: {
          headline: title,
          description,
        },
      },
    }
  }
)

export const getBreadcrumbSchema = createSelector(
  [getAccountUserID, getFullName],
  (artistUserId, artistName) => {
    if (!process.env.SITE_URL) {
      throw new Error('Please define SITE_URL in .env')
    }

    const siteUrl = process.env.SITE_URL
    const capitalizedArtistName = capitalizeString(artistName)

    return {
      '@context': 'http://schema.org',
      type: 'BreadcrumbList',
      itemListElement: [
        {
          type: 'ListItem',
          position: 1,
          item: {
            '@id': `${siteUrl}/account/profile/${artistUserId}`,
            name: `${capitalizedArtistName} Artist Profile`,
          },
        },
        {
          type: 'ListItem',
          position: 2,
          item: {
            '@id': `${siteUrl}/account/artworks/${artistUserId}`,
            name: `${capitalizedArtistName} Artworks`,
          },
        },
      ],
    }
  }
)

export const getHitsCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number => results.length
)
export const getHitsAvailableForSaleCount = createSelector(
  [getAlgoliaDataHitsResults],
  (results): number =>
    results.reduce((count, { originalStatus }) => (count += originalStatus === 'avail' ? 1 : 0), 0)
)

// {"@context":"http://schema.org","@type":"BreadcrumbList","itemListElement":[{"type":"ListItem","position":1,"item":{"@id":"https://www.qa.saatchiart.com","name":"Home"}},{"type":"ListItem","position":2,"item":{"@id":"https://www.qa.saatchiart.com/account-artworks","name":"Account/Artworks"}}]}
export const getAllArtworksMetaData = createSelector(
  [
    getCountry,
    getFullName,
    getBreadcrumbSchema,
    getHitsAvailableForSaleCount,
    getHitsCount,
    getAvatar,
    getAlgoliaDataPaginationCurrent,
    getAlgoliaDataPaginationTotal,
  ],
  (
    artistCountry,
    artistName,
    breadcrumb,
    availableForSaleCount,
    hitsCount,
    avatarURL,
    currentPage,
    totalPages
  ): Record<string, any> => {
    const capitalizedArtistName = capitalizeString(artistName)
    const description = `View ${capitalizedArtistName}'s artworks on Saatchi Art. Find original art for sale at great prices, including Paintings, Sculptures, Photography, Drawings, and Art Prints from emerging artists like ${capitalizedArtistName}`
    const title = `${capitalizedArtistName} Artworks | Saatchi Art`
    return {
      description,
      title,
      og: {
        description,
        image: avatarURL,
        title,
        type: 'article',
      },
      page: {
        current: currentPage,
        prev: currentPage === 2 || currentPage > 2,
        next: currentPage !== totalPages,
      },
      preloadImages: [],
      robots: hitsCount > 10 && Boolean(availableForSaleCount),
      // if PRODUCTS for Sale
      schema: {
        breadcrumb,
        webpage: {
          description,
          headline: title,
        },
      },
    }
  }
)
export const getFeaturedCollections = createSelector(
  [getAccountData],
  (accountData): ReadonlyArray<CollectionType> => accountData.featuredCollections || []
)
